import React from 'react';
import { Info, Utensils, Calculator, Target, Apple, AlertTriangle, Coffee, BarChart2, Droplets } from 'lucide-react';

/**
 * Компонент с информационным контентом о правильном питании и снижении веса
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о правильном питании и снижении веса
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Как работает снижение веса с точки зрения науки
        </h3>
        <p>
          Снижение веса основано на простом принципе энергетического баланса: если вы потребляете меньше калорий, чем расходуете, 
          организм начинает использовать запасы жира. Дефицит в 7700 калорий примерно равен потере 1 кг жировой ткани.
        </p>
        <p>
          Однако на практике это не всегда линейный процесс. Метаболизм адаптируется к изменениям в питании, а потеря веса часто 
          замедляется со временем. Для устойчивого результата важно не только создать дефицит калорий, но и делать это 
          правильно, обеспечивая организм всеми необходимыми питательными веществами.
        </p>

        <h3 className="flex items-center gap-2">
          <Target className="w-5 h-5 text-emerald-500" />
          Почему умеренный темп снижения веса лучше быстрого
        </h3>
        <p>
          Оптимальным темпом снижения веса считается 0.5-1 кг в неделю. Такой подход имеет несколько преимуществ:
        </p>
        <ul>
          <li>Снижается риск потери мышечной массы вместо жировой</li>
          <li>Меньше метаболическая адаптация, которая замедляет дальнейшее снижение веса</li>
          <li>Ниже стресс для организма, особенно для сердечно-сосудистой системы</li>
          <li>Формируются устойчивые пищевые привычки, что помогает сохранить результат</li>
          <li>Меньше риск дефицита питательных веществ и связанных с этим проблем со здоровьем</li>
        </ul>
        <p>
          Исследования показывают, что 95% людей, быстро сбросивших вес на жестких диетах, возвращаются к исходному
          весу в течение 1-5 лет. В то время как умеренное снижение веса с изменением образа жизни показывает
          гораздо более устойчивые результаты.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Особенности национального питания</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            В России традиционная кухня часто высококалорийна, с большим количеством картофеля, хлеба и жирных продуктов. 
            При этом у нас доступны прекрасные источники белка (творог, рыба), полезных углеводов (гречка, овсянка) и овощей (капуста, свекла).
            Стремитесь адаптировать традиционные рецепты, делая их более полезными, а не полностью отказываться от привычной пищи.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Utensils className="w-5 h-5 text-emerald-500" />
          Основы сбалансированного рациона
        </h3>
        <p>
          Сбалансированный рацион для снижения веса должен включать все группы продуктов и обеспечивать организм
          необходимыми питательными веществами:
        </p>
        <ul>
          <li><strong>Белки (1.5-2 г на кг веса)</strong> - необходимы для сохранения мышечной массы при снижении веса. 
            Источники: нежирное мясо, птица, рыба, творог, яйца, бобовые.</li>
          <li><strong>Жиры (0.8-1 г на кг веса)</strong> - важны для усвоения жирорастворимых витаминов и гормонального баланса. 
            Источники: оливковое масло, орехи, семена, авокадо, жирная рыба.</li>
          <li><strong>Углеводы (оставшиеся калории)</strong> - предпочтение сложным углеводам с низким гликемическим индексом. 
            Источники: овощи, фрукты, цельнозерновые крупы, бобовые.</li>
          <li><strong>Клетчатка (25-35 г в день)</strong> - улучшает пищеварение, повышает сытость. 
            Источники: овощи, фрукты, отруби, цельнозерновые продукты.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Apple className="w-5 h-5 text-emerald-500" />
          Планирование приемов пищи
        </h3>
        <p>
          Правильное распределение пищи в течение дня помогает контролировать голод и поддерживать стабильный уровень энергии:
        </p>
        <ul>
          <li><strong>Завтрак (20-25% дневных калорий)</strong> - должен содержать достаточно белка и сложных углеводов для долгой сытости.</li>
          <li><strong>Обед (30-35% дневных калорий)</strong> - основной прием пищи, включающий белки, полезные жиры и углеводы.</li>
          <li><strong>Ужин (20-25% дневных калорий)</strong> - легкий, но питательный, с акцентом на белки и овощи.</li>
          <li><strong>Перекусы (15-25% дневных калорий)</strong> - небольшие порции, помогающие поддерживать чувство сытости между основными приемами пищи.</li>
        </ul>
        <p>
          Исследования показывают, что регулярное питание каждые 3-4 часа помогает поддерживать нормальный уровень сахара в крови 
          и предотвращает переедание во время основных приемов пищи.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Индивидуальные особенности
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Скорость метаболизма может различаться у разных людей на 5-8% из-за генетических факторов</li>
            <li>Некоторые заболевания (гипотиреоз, СПКЯ, сахарный диабет) влияют на обмен веществ</li>
            <li>Возрастные изменения (после 30 лет метаболизм снижается примерно на 2-3% каждые 10 лет)</li>
            <li>Прием некоторых лекарств может влиять на аппетит или метаболизм</li>
            <li>Стресс может приводить к повышению уровня кортизола и накоплению жира в абдоминальной области</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Coffee className="w-5 h-5 text-emerald-500" />
          Значение питьевого режима для похудения
        </h3>
        <p>
          Достаточное потребление воды играет важную роль в процессе снижения веса:
        </p>
        <ul>
          <li>Увеличивает расход энергии (термогенный эффект)</li>
          <li>Помогает контролировать аппетит (чувство голода часто путают с жаждой)</li>
          <li>Улучшает обмен веществ и выведение продуктов распада</li>
          <li>Оптимизирует работу всех систем организма</li>
        </ul>
        <p>
          Рекомендуемый объем воды составляет 30-35 мл на кг веса тела, с поправкой на физическую активность, 
          температуру окружающей среды и индивидуальные особенности. Для человека весом 70 кг это около 2-2.5 литров в день.
        </p>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-emerald-500" />
          Отслеживание прогресса
        </h3>
        <p>
          Регулярное отслеживание результатов помогает оставаться мотивированным и вовремя корректировать подход:
        </p>
        <ul>
          <li><strong>Взвешивание:</strong> 1-2 раза в неделю в одинаковых условиях (утром, натощак, после туалета)</li>
          <li><strong>Измерения:</strong> объем талии, бедер, груди каждые 2-4 недели</li>
          <li><strong>Фотографии:</strong> визуальные изменения часто заметнее цифр на весах</li>
          <li><strong>Дневник питания:</strong> помогает осознать реальное потребление калорий и питательных веществ</li>
          <li><strong>Самочувствие:</strong> энергия, качество сна, работоспособность, настроение</li>
        </ul>
        <p>
          Помните, что вес может колебаться на 1-2 кг в течение дня из-за уровня гидратации, содержимого кишечника 
          и гормональных изменений. Оценивайте тенденцию за 2-4 недели, а не ежедневные колебания.
        </p>

        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-700 dark:text-emerald-300 font-medium flex items-center gap-2">
            <Droplets className="w-4 h-4" />
            Рекомендации по питьевому режиму в России
          </h4>
          <p className="text-emerald-600 dark:text-emerald-400 text-sm mb-0">
            В России есть свои особенности питьевого режима, связанные с климатом и традициями. Зимой, когда 
            воздух в помещениях сухой из-за отопления, потребность в воде может быть выше, несмотря на холод. 
            Традиционные напитки, такие как чай, компот, морс (без добавления сахара), также вносят вклад в 
            гидратацию, но не заменяют чистую воду полностью. Старайтесь выпивать не менее 1.5-2 литров 
            чистой воды в день, независимо от сезона.
          </p>
        </div>

        <h3>Распространенные мифы о похудении</h3>
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 dark:border-gray-700 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-left">Миф</th>
              <th className="border border-gray-300 dark:border-gray-700 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-left">Факт</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Необходимо исключить все углеводы для снижения веса</td>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Сложные углеводы (цельнозерновые, овощи, фрукты) необходимы для энергии и питательных веществ. Исключать стоит простые углеводы (сахар, белая мука).</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Нельзя есть после 18:00</td>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Важно общее количество калорий за день, а не время их потребления. Однако легкий ужин за 2-3 часа до сна предпочтительнее.</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Необходимы детокс-программы для очищения организма</td>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Печень и почки прекрасно справляются с очищением организма. Нет научных доказательств эффективности "детокса".</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Жиры всегда вредны для похудения</td>
              <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">Полезные жиры (орехи, авокадо, оливковое масло) необходимы для здоровья и поддержания чувства сытости.</td>
            </tr>
          </tbody>
        </table>

        <p className="font-semibold mt-6">
          Наш калькулятор калорий поможет вам рассчитать индивидуальную потребность в калориях и создать сбалансированный план питания,
          который позволит достичь ваших целей по снижению веса здоровым и устойчивым способом!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;